import React, {useCallback, useEffect, useMemo, useRef, useState} from "react";
import {AgGridReact} from "ag-grid-react";
import {useQuery} from "@tanstack/react-query";
import Modal from "../../../components/modal";
import Card from "../../../components/card";
import InputForm from "../../../components/input-form";
import Button from "../../../components/button";
import TrainingsService from "../../../services/training-service";
import general from "../../../utils/general";
import {normalizeQuickFilterText} from "../../../utils/ag-grid-quick-filter";
import {sortTrainingUsersByName} from "./training-users-utils";
import {MaskDefault} from "../../../utils/mask";

const mapUserToTrainingUser = (user) => {
    const cpf = user.cpf ? general.formatCpf(user.cpf) : null;
    const project = user.project_name ?? null;
    const resale = user.resale_name ?? null;

    return {
        user_id: user.id,
        name: user.user_name,
        cpf,
        project,
        resale,
    };
};

const SelectUserCheckbox = ({data, isSelected, handleToggle}) => {
    return (
        <input
            type="checkbox"
            checked={isSelected(data)}
            onChange={() => handleToggle(data)}
        />
    );
};

const TrainingUsersModal = ({isOpen, onClose, selectedUsers, onConfirm}) => {
    const gridRef = useRef();
    const [selectedRows, setSelectedRows] = useState([]);
    const [filters, setFilters] = useState({
        name: "",
        cpf: "",
        project: "",
        resale: "",
    });

    const {data: usersResponse, isFetching} = useQuery({
        queryFn: () => TrainingsService.users(),
        queryKey: ["training-users-modal"],
        enabled: isOpen,
    });

    const users = useMemo(() => {
        return (usersResponse?.data ?? []).map(mapUserToTrainingUser);
    }, [usersResponse]);

    const filteredUsers = useMemo(() => {
        return users.filter(user => {
            return Object.entries(filters).every(([field, value]) => {
                if (!value) return true;

                return normalizeQuickFilterText(user[field]).includes(normalizeQuickFilterText(value));
            });
        });
    }, [filters, users]);

    useEffect(() => {
        if (isOpen) {
            setSelectedRows(selectedUsers ?? []);
            setFilters({
                name: "",
                cpf: "",
                project: "",
                resale: "",
            });
        }
    }, [isOpen, selectedUsers]);

    const selectedValues = useMemo(() => {
        return new Set(selectedRows.map(selected => selected.user_id));
    }, [selectedRows]);

    useEffect(() => {
        gridRef.current?.api?.refreshCells({force: true});
        gridRef.current?.api?.redrawRows();
    }, [selectedValues]);

    const isSelected = useCallback((user) => {
        return selectedValues.has(user?.user_id);
    }, [selectedValues]);

    const handleToggle = useCallback((user) => {
        setSelectedRows(prev => {
            if (prev.some(selected => selected.user_id === user.user_id)) {
                return prev.filter(selected => selected.user_id !== user.user_id);
            }

            return [...prev, user];
        });
    }, []);

    const handleFilterChange = useCallback((field) => ({target}) => {
        setFilters(prev => ({
            ...prev,
            [field]: target.value,
        }));
    }, []);

    const columnDefs = useMemo(() => [
        {
            field: "select",
            headerName: "",
            maxWidth: 55,
            cellStyle: {justifyContent: "center"},
            cellRenderer: SelectUserCheckbox,
            cellRendererParams: {
                isSelected,
                handleToggle,
            },
        },
        {field: "name", headerName: "Nome", flex: 1, minWidth: 180},
        {field: "cpf", headerName: "CPF", maxWidth: 120},
        {field: "project", headerName: "Projeto", minWidth: 100},
        {field: "resale", headerName: "Revenda", minWidth: 150},
    ], [handleToggle, isSelected]);

    const getRowStyle = useCallback(({data}) => {
        return isSelected(data) ? {backgroundColor: "#dbeafe"} : undefined;
    }, [isSelected]);

    const handleConfirm = () => {
        onConfirm(sortTrainingUsersByName(selectedRows));
        onClose();
    };

    return (
        <Modal isOpen={isOpen} onClose={onClose} title="Adicionar usuários" className="w-9/12">
            <div className="p-3">
                <Card>
                    <div className="mb-3 grid grid-cols-4 gap-3">
                        <InputForm
                            id="search-training-users-modal-name"
                            label="Nome"
                            type="search"
                            value={filters.name}
                            col=""
                            onChange={handleFilterChange("name")}
                        />

                        <InputForm
                            id="search-training-users-modal-cpf"
                            label="CPF"
                            type="search"
                            value={filters.cpf}
                            mask={MaskDefault.cpf}
                            col=""
                            onChange={handleFilterChange("cpf")}
                        />

                        <InputForm
                            id="search-training-users-modal-project"
                            label="Projeto"
                            type="search"
                            value={filters.project}
                            col=""
                            onChange={handleFilterChange("project")}
                        />

                        <InputForm
                            id="search-training-users-modal-resale"
                            label="Revenda"
                            type="search"
                            value={filters.resale}
                            col=""
                            onChange={handleFilterChange("resale")}
                        />
                    </div>

                    <div className="ag-theme-quartz h-[55vh] w-full">
                        <style>
                            {`
                                .training-user-selected .ag-cell {
                                    background-color: #dbeafe !important;
                                }
                            `}
                        </style>
                        <AgGridReact
                            ref={gridRef}
                            columnDefs={columnDefs}
                            rowData={filteredUsers}
                            defaultColDef={{resizable: true, sortable: true}}
                            overlayNoRowsTemplate={isFetching ? "Carregando usuários..." : "Nenhum usuário encontrado"}
                            getRowStyle={getRowStyle}
                            rowClassRules={{
                                "training-user-selected": ({data}) => isSelected(data),
                            }}
                        />
                    </div>

                    <div className="mt-3 flex justify-end">
                        <Button type="button" target="primary" onClick={handleConfirm}>
                            Confirmar
                        </Button>
                    </div>
                </Card>
            </div>
        </Modal>
    );
};

export default TrainingUsersModal;
